import React, { useState, useRef } from "react";
import styles from "./styles";
import compose from "recompose/compose";
import { withStyles } from '@mui/styles';
import { withSnackbar } from "notistack";
import { MailProDialog } from "../MailProDialog/MailProDialog";
import { MailProDialogHeader } from "../MailProDialog/MailProDialogHeader";
import { MailProDialogBody } from "../MailProDialog/MailProDialogBody";
import MailProButton from "../UI/MailProButton";
import MailProTextarea from "../UI/MailProTextarea";
import { GetStateStorage } from 'common/storage';
import helpUtils from "views/Help/HelpUtils";
import { normalizeTextComprehensive, handlePasteNormalization } from "common/helpers/textNormalizer";
import { LOG_MODULES, LOG_CODE_PREFIX, trackException } from "common/errorTracker";
import {
  appendFreshdeskTicketDefaults,
  getTeamMemberEmails,
  submitFreshdeskTicket,
} from "common/freshdesk";

const MailProSupportV2 = ({
    classes,
    accessedFromAreaID,
    accessedFromAreaName,
    children,
    agentData,
    ...rest
}) => {
    const [open, setOpen] = useState(false);
    const [helpMessage, setMessageData] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [isSubmitted, setSubmitted] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState({
        file: null,
        screenshot: null
    });
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    const { listgenUserFirstName } = agentData;

    const handleDataUpdate = (value) => {
        setMessageData(value);
    };

    const handleResubmit = () => {
        setSubmitted(false);
        setMessage({ type: '', text: '' });
    };

    const handleClose = () => {
        setOpen(false);
        setLoading(false);
        setSubmitted(false);
        setMessage({ type: '', text: '' });
        setFormData({ file: null, screenshot: null });
        setIsDragOver(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setFormData(prev => ({
                ...prev,
                file: file
            }));
        } else {
            setMessage({
                type: 'error',
                text: 'Please select an image file only.'
            });
        }
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            const file = files[0];
            if (file.type.startsWith('image/')) {
                setFormData(prev => ({
                    ...prev,
                    file: file
                }));
            } else {
                setMessage({
                    type: 'error',
                    text: 'Please drop an image file only.'
                });
            }
        }
    };

    const captureScreenshot = () => {
        if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
            // Use unique ID to find the support dialog specifically
            const dialogElement = document.getElementById('mailpro-support-v2-dialog-lg');
            const backdropElement = document.querySelector('.MuiBackdrop-root');
            const dialogPaper = dialogElement ? dialogElement.querySelector('.MuiDialog-paper') : null;

            // Store original styles
            const originalDialogDisplay = dialogElement ? dialogElement.style.display : '';
            const originalBackdropDisplay = backdropElement ? backdropElement.style.display : '';
            const originalPaperDisplay = dialogPaper ? dialogPaper.style.display : '';

            // Hide all dialog elements
            if (dialogElement) {
                dialogElement.style.display = 'none';
            }
            if (backdropElement) {
                backdropElement.style.display = 'none';
            }
            if (dialogPaper) {
                dialogPaper.style.display = 'none';
            }

            navigator.mediaDevices.getDisplayMedia({
                video: {
                    mediaSource: 'screen',
                    width: { ideal: 1920 },
                    height: { ideal: 1080 }
                },
                audio: false
            })
                .then(stream => {
                    const video = document.createElement('video');
                    video.srcObject = stream;
                    video.style.position = 'fixed';
                    video.style.top = '-9999px';
                    video.style.left = '-9999px';
                    document.body.appendChild(video);

                    video.play();

                    // Wait for video to be ready and add delay for Windows
                    video.addEventListener('loadedmetadata', () => {
                        // Add delay to ensure proper capture on Windows
                        setTimeout(() => {
                            const canvas = document.createElement('canvas');
                            canvas.width = video.videoWidth;
                            canvas.height = video.videoHeight;
                            const ctx = canvas.getContext('2d');
                            ctx.drawImage(video, 0, 0);

                            canvas.toBlob(blob => {
                                const file = new File([blob], 'screenshot.png', { type: 'image/png' });
                                setFormData(prev => ({
                                    ...prev,
                                    screenshot: file
                                }));

                                // Clean up
                                stream.getTracks().forEach(track => track.stop());
                                document.body.removeChild(video);

                                // Restore dialog and backdrop visibility
                                if (dialogElement) {
                                    dialogElement.style.display = originalDialogDisplay;
                                }
                                if (backdropElement) {
                                    backdropElement.style.display = originalBackdropDisplay;
                                }
                                if (dialogPaper) {
                                    dialogPaper.style.display = originalPaperDisplay;
                                }
                            }, 'image/png', 0.9);
                        }, 1000); // 1 second delay for Windows
                    });
                })
                .catch(err => {
                    console.error('Screen capture error:', err);
                    setMessage({
                        type: 'error',
                        text: 'Screen capture not supported or denied.'
                    });

                    // Restore dialog and backdrop visibility on error
                    if (dialogElement) {
                        dialogElement.style.display = originalDialogDisplay;
                    }
                    if (backdropElement) {
                        backdropElement.style.display = originalBackdropDisplay;
                    }
                    if (dialogPaper) {
                        dialogPaper.style.display = originalPaperDisplay;
                    }
                });
        } else {
            setMessage({
                type: 'error',
                text: 'Screen capture not supported in this browser.'
            });
        }
    };

    const submitTicket = async () => {
        setLoading(true);

        try {
            const stateData = GetStateStorage();
            const agentData = ((stateData || {}).userData) || {};

            // Create FormData for file upload
            const formDataToSend = new FormData();

      // Get team member emails for CC
      const teamMemberEmails = getTeamMemberEmails(agentData);
      
      // Get city and area information using helpUtils
      const dataFn = () => agentData;
      const { cityName, areaNames } = helpUtils.getAgentAreasandCityName(
        accessedFromAreaID,
        dataFn
      );
      
      // Normalize the help message
      const normalizedHelpMessage = normalizeTextComprehensive(helpMessage);

            // Basic ticket information
            formDataToSend.append('name', agentData.listgenUserFirstName + " " + agentData.listgenUserLastName);
            formDataToSend.append('email', agentData.emailAddress);
            formDataToSend.append('subject', helpUtils.subjectBuilder(() => agentData));

      // Format the description with agent information
      const formattedDescription = `<b>Listgen User ID:</b>\t${agentData.listgenUserID || 'N/A'}<br/><br/><b>Full Name:</b>\t${agentData.listgenUserFirstName} ${agentData.listgenUserLastName}<br/><br/><b>Email:</b>\t${agentData.emailAddress || 'N/A'}<br/><br/><b>Phone #:</b>\t${agentData.mobilePhoneNo || 'N/A'}<br/><br/><b>City:</b>\t${cityName || 'N/A'}<br/><br/><b>Area(s):</b>\t${areaNames || accessedFromAreaName || 'N/A'}<br/><br/><b>Message:</b><br/>${normalizedHelpMessage}`;

            formDataToSend.append('description', formattedDescription);

            // Add CC emails for all team members
            teamMemberEmails.forEach(email => {
                formDataToSend.append('cc_emails[]', email);
            });

            appendFreshdeskTicketDefaults(formDataToSend);

            // Add attachments if files are selected
            if (formData.file) {
                formDataToSend.append('attachments[]', formData.file);
            }
            if (formData.screenshot) {
                formDataToSend.append('attachments[]', formData.screenshot);
            }

            await submitFreshdeskTicket(formDataToSend);

            const ccCount = teamMemberEmails.length;
            const teamNotificationText = ccCount > 0 ? ` Your team (${ccCount} members) has been notified.` : '';
            setMessage({
                type: 'success',
                text: <p>Your support request has been submitted successfully. <br/> One of our team members will respond to you as quickly as possible.<br/>{teamNotificationText}</p>
            });
            setSubmitted(true);
            // Reset form after successful submission (but keep success message visible)
            setTimeout(() => {
                setFormData({ file: null, screenshot: null });
                setMessageData("");
                setIsDragOver(false);
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            }, 2000);
        } catch (error) {
            console.error('Error submitting support request:', error);
            trackException({
                module: LOG_MODULES.MAILPRO,
                code: `${LOG_CODE_PREFIX.MAILPRO}SUPPORT_TICKET_FAILED`,
                error,
                httpStatus: error && (error.httpStatus || (error.response && error.response.status)),
                data: {
                    accessedFromAreaID,
                    hasAttachment: Boolean(formData.file || formData.screenshot),
                },
            });
            setMessage({
                type: 'error',
                text: 'Failed to submit support request. Please try again.'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <style>
                {`
          @keyframes successPulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.02); }
            100% { transform: scale(1); }
          }
        `}
            </style>
            <div onClick={() => setOpen(true)}>
                {children ? (
                    children
                ) : (
                    <MailProButton
                        variant="filled"
                        label={"Support"}
                        customStyle={{
                            fontSize: "12px",
                            fontWeight: 600,
                            backgroundColor: "white",
                            color: "black",
                        }}
                        onClick={() => { }}
                        title="Need any Help? Click here to open Help"
                    />
                )}
            </div>
            {open && (
                <MailProDialog
                    open={open}
                    classes={{
                        paper: classes.mailproSupportDialogPaper,
                    }}
                    style={{ boxShadow: 'none' }}
                    id="mailpro-support-v2-dialog-lg"
                >
                    <MailProDialogHeader>
                        <div className={classes.mailproSupportHeader}>
                            <div className={classes.mailproSupportHeaderFullName}>
                                {!isSubmitted ? `ListGen SUPPORT` : `SUPPORT REQUEST RECEIVED`}
                            </div>
                        </div>
                    </MailProDialogHeader>
                    <MailProDialogBody style={{ overflow: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
                        <div style={{ fontFamily: 'objektiv-mk2, sans-serif', position: 'relative' }}>
                            {/* Success/Error Messages */}
                            {message.type && (
                                <div style={{
                                    backgroundColor: message.type === 'success' ? '#d4edda' : '#f8d7da',
                                    border: `1px solid ${message.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                                    color: message.type === 'success' ? '#155724' : '#721c24',
                                    display: 'flex',
                                    alignItems: 'center',
                                    borderRadius: '8px',
                                    padding: '16px',
                                    marginBottom: '20px',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    animation: message.type === 'success' ? 'successPulse 0.6s ease-in-out' : 'none'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                        <div style={{
                                            backgroundColor: message.type === 'success' ? '#28a745' : '#dc3545',
                                            color: 'white',
                                            borderRadius: '50%',
                                            width: '24px',
                                            height: '24px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            marginRight: '12px',
                                            fontSize: '14px',
                                            fontWeight: 'bold',
                                            flexShrink: 0,
                                            minWidth: '24px',
                                            minHeight: '24px'
                                        }}>
                                            {message.type === 'success' ? '✓' : '✗'}
                                        </div>
                                        <span style={{
                                            fontWeight: '500',
                                            fontFamily: 'objektiv-mk2, sans-serif',
                                            fontSize: message.type === 'success' ? '16px' : '14px'
                                        }}>
                                            {message.text}
                                        </span>
                                    </div>
                                </div>
                            )}

                            {!isSubmitted ? (
                                <div>
                                    <div className={classes.mailproSupportTitle}>
                                        <div className={classes.mailproSupportAgentName}>
                                            Greetings, {listgenUserFirstName}
                                        </div>
                                        <div style={{ marginTop: "8px" }}>
                                            Please type in your SUPPORT issue and/or questions.
                                        </div>
                                        <div style={{ marginTop: "8px" }}>
                                            One of our team members will respond to you as quickly as
                                            possible.
                                        </div>
                                    </div>
                                    <div>
                                        <MailProTextarea
                                            rows={4}
                                            fullWidth={true}
                                            onChange={(event) => handleDataUpdate(event.target.value)}
                                            value={helpMessage}
                                            placeholder={"Type here..."}
                                            onPaste={(event) => handlePasteNormalization(event, helpMessage, handleDataUpdate)}
                                        />
                                    </div>

                                    {/* File Upload Section */}
                                    <div style={{ marginTop: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                            Attach Files (Optional):
                                        </label>
                                        <div
                                            onDragOver={handleDragOver}
                                            onDragLeave={handleDragLeave}
                                            onDrop={handleDrop}
                                            onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                            style={{
                                                width: '95%',
                                                padding: '12px',
                                                border: `2px dashed ${isDragOver ? '#4e85af' : '#666'}`,
                                                borderRadius: '8px',
                                                backgroundColor: isDragOver ? '#f0f8ff' : '#fafafa',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                fontFamily: 'objektiv-mk2, sans-serif',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                minHeight: '70px',
                                                textAlign: 'center'
                                            }}
                                        >
                                            <div style={{ color: '#666', fontSize: '14px', marginBottom: '4px' }}>
                                                📁 Drag & drop files here or click to browse
                                            </div>
                                            <div style={{ color: '#999', fontSize: '12px' }}>
                                                Only image files are allowed
                                            </div>
                                        </div>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            style={{ display: 'none' }}
                                        />
                                        {formData.file && (
                                            <p style={{ marginTop: '8px', fontSize: '12px', color: '#666', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                                Selected: {formData.file.name}
                                            </p>
                                        )}
                                    </div>

                                    {/* Screen Capture Section */}
                                    <div style={{ marginTop: '20px' }}>
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                            Screen Capture (Optional):
                                        </label>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <button
                                                onClick={captureScreenshot}
                                                disabled={isLoading}
                                                style={{
                                                    padding: '8px 16px',
                                                    border: '1px solid #4e85af',
                                                    borderRadius: '4px',
                                                    backgroundColor: isLoading ? '#f5f5f5' : 'white',
                                                    color: isLoading ? '#999' : '#4e85af',
                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                    fontFamily: 'objektiv-mk2, sans-serif',
                                                    fontSize: '14px',
                                                    opacity: isLoading ? 0.6 : 1
                                                }}
                                            >
                                                {isLoading ? '⏳ Capturing...' : '📷 Capture Screen'}
                                            </button>
                                            {formData.screenshot && (
                                                <div style={{ marginTop: '8px' }}>
                                                    <span style={{ fontSize: '12px', color: '#666', fontFamily: 'objektiv-mk2, sans-serif', display: 'block', marginBottom: '8px' }}>
                                                        Screenshot captured: {formData.screenshot.name}
                                                    </span>
                                                    <div style={{
                                                        border: '1px solid #ddd',
                                                        borderRadius: '4px',
                                                        padding: '8px',
                                                        backgroundColor: '#f9f9f9',
                                                        maxWidth: '300px',
                                                        maxHeight: '200px',
                                                        overflow: 'hidden'
                                                    }}>
                                                        <img
                                                            src={URL.createObjectURL(formData.screenshot)}
                                                            alt="Captured Screenshot"
                                                            style={{
                                                                width: '100%',
                                                                height: 'auto',
                                                                maxHeight: '180px',
                                                                objectFit: 'contain',
                                                                borderRadius: '2px'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        <div style={{ fontSize: '11px', color: '#888', marginTop: '4px', fontFamily: 'objektiv-mk2, sans-serif' }}>
                                            Note: This dialog will be temporarily hidden during capture
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div>
                                    <div className={classes.mailproSupportTitle}>
                                        {/* Success message is now only shown in the alert above */}
                                    </div>
                                </div>
                            )}
                        </div>
                        {!isSubmitted ? (
                            <div className={classes.footer}>
                                <div>
                                    <MailProButton
                                        key={"mp_new_ticket_cancel"}
                                        variant="outlined"
                                        label={"CANCEL"}
                                        customStyle={{
                                            fontSize: "14px",
                                            fontWeight: 700,
                                            padding: "12px 24px",
                                            minWidth: "120px"
                                        }}
                                        onClick={handleClose}
                                        disabled={isLoading}
                                    />
                                </div>
                                <div style={{ marginLeft: "-15%" }}>
                                    <MailProButton
                                        key={"mp_new_first_ticket_group"}
                                        variant="filled"
                                        label={"SUBMIT"}
                                        customStyle={{
                                            fontSize: "14px",
                                            fontWeight: 700,
                                            padding: "12px 24px",
                                            minWidth: "120px"
                                        }}
                                        onClick={submitTicket}
                                        disabled={isLoading || !helpMessage}
                                    />
                                </div>
                                <div></div>
                            </div>
                        ) : (
                            <div className={classes.footer}>
                                <div></div>
                                <div>
                                    <MailProButton
                                        key={"mp_new_add_close_cance;"}
                                        variant="outlined"
                                        label={"Close"}
                                        customStyle={{
                                            fontSize: "14px",
                                            fontWeight: 700,
                                            padding: "12px 24px",
                                            minWidth: "120px"
                                        }}
                                        onClick={handleClose}
                                    />
                                </div>
                                <div></div>
                            </div>
                        )}
                    </MailProDialogBody>
                </MailProDialog>
            )}
        </div>
    );
};

export default compose(withStyles(styles), withSnackbar)(MailProSupportV2);
